import { ArrowRight, Loader2 } from 'lucide-react';
import {
  diffProposal,
  targetDateLabel,
  type ProposalChange,
  type ProposalView,
} from '@/lib/reviewProposal';
import type { Child, ReviewItem } from '@/types';
import { PROPOSAL_COPY as COPY } from './proposalCopy';

/**
 * Body of a Review card whose item PROPOSES a change to something already on
 * the schedule (YAR-493). Rendered by EventGroupCard / TaskCard in place of
 * their usual title + date rows; the card shell (icon, edit-on-tap, member
 * avatars, nested tasks) stays the same.
 *
 * The label is real text — "Update" / "Cancel" — so the state never rests on
 * colour alone.
 */
export function ProposalSummary({
  item,
  view,
  childrenData,
  timezone,
  onKeepAsNew,
  keepingAsNew = false,
}: {
  item: ReviewItem;
  view: Exclude<ProposalView, { kind: 'none' }>;
  childrenData: Child[];
  timezone?: string | null;
  /** Omit to hide "Keep as new" (read-only viewers, committed emails). */
  onKeepAsNew?: (item: ReviewItem) => void;
  keepingAsNew?: boolean;
}) {
  const isCancel = view.action === 'cancel';
  const cancelDate =
    view.kind === 'resolved' ? targetDateLabel(view.target, timezone) : null;

  return (
    <div className="flex flex-col gap-2">
      <span
        className={[
          'inline-flex items-center w-fit px-2 py-0.5 rounded-full border text-xs font-semibold',
          isCancel
            ? 'bg-background border-border-error text-text-error'
            : 'bg-info-bg border-info-border text-info-text',
        ].join(' ')}
      >
        {isCancel ? COPY.cancelLabel : COPY.updateLabel}
      </span>

      {view.kind === 'gone' ? (
        <>
          <span className="text-base font-semibold text-foreground break-words">
            {item.title}
          </span>
          <p className="text-sm text-muted-foreground m-0">{COPY.cancelGone}</p>
        </>
      ) : view.kind === 'unverified' ? (
        <>
          <p className="text-sm text-foreground m-0 break-words">
            {isCancel ? COPY.cancelLeadUnverified : COPY.updateLeadUnverified}
          </p>
          <span className="text-base font-semibold text-foreground break-words">
            {item.title}
          </span>
          {isCancel && (
            <p className="text-sm text-muted-foreground m-0">
              {COPY.cancelUnverifiedEffect}
            </p>
          )}
        </>
      ) : isCancel ? (
        <>
          <p className="text-base text-foreground m-0 break-words">
            <span className="font-semibold">{view.target.title}</span>
            {cancelDate ? ` (${cancelDate})` : null}
          </p>
          <p className="text-sm text-muted-foreground m-0">
            {view.target.kind === 'event'
              ? COPY.cancelEventEffect
              : COPY.cancelTaskEffect}
          </p>
        </>
      ) : (
        <>
          <p className="text-sm text-foreground m-0 break-words">
            {COPY.updateLead}{' '}
            <span className="font-semibold">{view.target.title}</span>
          </p>
          <ChangeList
            changes={diffProposal(item, view.target, childrenData, timezone)}
          />
        </>
      )}

      {!isCancel && onKeepAsNew && (
        <button
          type="button"
          aria-label={COPY.keepAsNewAria(item.title)}
          disabled={keepingAsNew}
          onClick={(e) => {
            // The card itself opens the edit modal on click / Enter / Space.
            e.stopPropagation();
            onKeepAsNew(item);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 self-start rounded-md border border-border bg-background px-3 py-1.5 text-sm font-semibold text-muted-foreground cursor-pointer hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {keepingAsNew && (
            <Loader2
              size={14}
              className="animate-spin"
              aria-hidden
            />
          )}
          {COPY.keepAsNew}
        </button>
      )}
    </div>
  );
}

function ChangeList({ changes }: { changes: ProposalChange[] }) {
  if (changes.length === 0) {
    return (
      <p className="text-sm text-muted-foreground m-0">{COPY.noChanges}</p>
    );
  }
  return (
    <dl className="flex flex-col gap-2 m-0">
      {changes.map((c) => (
        <div
          key={c.field}
          className="flex flex-col gap-0.5 min-w-0"
          data-testid={`proposal-change-${c.field}`}
        >
          <dt className="text-xs font-medium text-muted-foreground">
            {COPY.fieldLabel[c.field]}
          </dt>
          <dd className="m-0 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm min-w-0">
            <span className="text-muted-foreground line-through break-words min-w-0">
              <span className="sr-only">{COPY.srBefore} </span>
              {c.before ?? COPY.emptyValue[c.field]}
            </span>
            <ArrowRight
              size={14}
              className="text-muted-foreground flex-shrink-0"
              aria-hidden
            />
            <span className="font-semibold text-foreground break-words min-w-0">
              <span className="sr-only">{COPY.srAfter} </span>
              {c.after}
            </span>
          </dd>
        </div>
      ))}
    </dl>
  );
}
